import { downloadCsvFile } from "@/features/custom_downloads";
import { Button, Segmented, Select, theme } from "antd";
import { useEffect, useState } from "react";
import { yearLong } from "../format";
import { cellText, csvName, csvRows, MAX_POINTS, metricOf, tableModel, type Layout, type Selection, type Series, type Sort } from "../model";
import type { Summary } from "../types";
import { Field, ScenarioPicker } from "./parts";
interface Props {
  summary: Summary;
  selection: Selection;
  series: (Series | null)[];
  cells: ({
    moved: boolean;
  } | null)[];
  /** Per location, whether its numbers could not be loaded. */
  failed: boolean[];
  /** Some location's numbers are still loading. */
  loading: boolean;
  dark: boolean;
  onChange: (change: Partial<Selection> | ((s: Selection) => Selection)) => void;
}
const LAYOUTS: {
  value: Layout;
  label: string;
}[] = [{
  value: "year",
  label: "Locations × years"
}, {
  value: "ssp",
  label: "Locations × scenarios"
}, {
  value: "all",
  label: "Locations × years × scenarios"
}];

/** The numbers behind the chart for every location, in one of three layouts, sortable, as CSV too. */
export const DataTable = ({
  summary,
  selection,
  series,
  cells,
  failed,
  loading,
  dark,
  onChange
}: Props) => {
  const {
    token
  } = theme.useToken();
  const [sort, setSort] = useState<Sort>({
    col: null,
    dir: 1
  });
  const m = metricOf(summary, selection.metric);
  // A new metric or layout starts unsorted, as the dashboard does.
  useEffect(() => setSort({
    col: null,
    dir: 1
  }), [selection.metric, selection.layout]);
  const t = tableModel(summary, selection, series, cells, sort);
  const left = (j: number) => j === 1 || j >= 4 && j < t.nLead;
  return <section className="flex min-w-0 flex-col bg-surface lg:col-span-2" data-sentry-component="DataTable" data-sentry-source-file="DataTable.tsx">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 border-0 border-b border-solid border-gray-100 px-[18px] py-2.5">
        <Field label="Table layout" data-sentry-element="Field" data-sentry-source-file="DataTable.tsx">
          <Segmented value={selection.layout} options={LAYOUTS} onChange={layout => onChange({
          layout: layout as Layout
        })} data-sentry-element="Segmented" data-sentry-source-file="DataTable.tsx" />
        </Field>
        {selection.layout === "year" && <Field label="Scenario">
            <ScenarioPicker summary={summary} metric={m} value={selection.tScen} dark={dark} onChange={tScen => onChange({
          tScen
        })} />
          </Field>}
        {selection.layout === "ssp" && <Field label="Year">
            <Select className="min-w-64" value={selection.tH} options={summary.horizons.flatMap((h, k) => k ? [{
          value: k,
          label: yearLong(h)
        }] : [])} onChange={(tH: number) => onChange({
          tH
        })} aria-label="Year" />
          </Field>}
        <span className="flex-1" />
        <Button type="primary"
      // The file would hold blanks for a location still loading or that could not be loaded.
      disabled={!selection.points.length || loading || failed.some(Boolean)} onClick={() => downloadCsvFile(csvRows(t), {
        filename: csvName(summary, selection)
      })} data-sentry-element="Button" data-sentry-source-file="DataTable.tsx">
          Download CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        {!selection.points.length ? <div className="px-[18px] py-[30px] text-center text-[12.5px] text-gray-500">
            No locations yet. Click the map or add a location; up to {MAX_POINTS} can be compared.
          </div> : <table className="w-full border-collapse text-xs">
            <caption className="caption-top px-[18px] pb-1.5 pt-2 text-left text-xs text-gray-600">{t.caption}</caption>
            <thead>
              <tr>
                {t.head.map((h, j) => <th key={h} className={`sticky top-0 cursor-pointer select-none whitespace-nowrap border-0 border-b border-solid border-gray-100 bg-surface px-[9px] py-[5px] text-[10.5px] font-semibold text-gray-500 ${left(j) ? "text-left" : "text-right"}`} aria-sort={sort.col === j ? sort.dir > 0 ? "ascending" : "descending" : undefined} onClick={() => setSort(s => s.col === j ? {
              col: j,
              dir: s.dir > 0 ? -1 : 1
            } : {
              col: j,
              dir: j >= t.nLead ? -1 : 1
            })}>
                    {h}
                    {sort.col === j ? sort.dir > 0 ? " ▲" : " ▼" : ""}
                  </th>)}
                <th className="border-0 border-b border-solid border-gray-100" />
              </tr>
            </thead>
            <tbody>
              {t.rows.map(r => <tr key={`${r.k}:${r.scenario ?? ""}`} aria-current={r.k === selection.sel} className="cursor-pointer hover:bg-gray-50" style={r.k === selection.sel ? {
            background: token.colorPrimaryBg
          } : undefined} onClick={() => onChange({
            sel: r.k
          })}>
                  {[...r.lead, ...r.numbers.map(v => cellText(v, selection.vt))].map((c, j) => <td key={j} className={`whitespace-nowrap border-0 border-b border-solid border-gray-100 px-[9px] py-[5px] tabular-nums ${left(j) ? "text-left" : "text-right"}`}>
                      {c}
                      {j === 1 && r.moved && <span className="text-gray-500"> (nearest cell with data)</span>}
                      {j === 1 && failed[r.k] && <span className="text-red-600"> (could not load)</span>}
                    </td>)}
                  <td className="border-0 border-b border-solid border-gray-100 px-1">
                    <button type="button" className="cursor-pointer border-0 bg-transparent px-1 text-[13px] text-gray-500 hover:text-red-600" title="Remove this location" aria-label={`Remove ${r.point.name}`} onClick={e => {
                e.stopPropagation();
                onChange(s => ({
                  ...s,
                  points: s.points.filter((_, k) => k !== r.k),
                  sel: s.sel > r.k ? s.sel - 1 : s.sel
                }));
              }}>
                      ×
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>
      <p className="m-0 px-4 pb-3 pt-2 text-xs leading-normal text-gray-500">
        <b className="text-gray-600">How to read the table.</b> The same numbers as the chart, for every location.
        Choose a layout: locations × years for one scenario, locations × scenarios for one year, or every year and
        scenario at once. Click a column heading to sort, for example by 2050 to rank locations. The table rounds to
        four significant figures, or to a whole number from 1,000 up; the download holds every digit the data has.
      </p>
    </section>;
};